import { EmailIcon } from "@chakra-ui/icons";
import { Box, Checkbox, Flex, Grid, GridItem, HStack, Link, Spacer, Text } from "@chakra-ui/react";
import { ComponentType, ReactNode, useContext, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { IdentityProvider } from "@nens/rana/api-v1";
import { useLocalStorage, useRect } from "@nens/react/hooks";

import { ACCOUNT_HAS_NO_ACCESS_SEARCH_PARAM } from "../../auth/auth-constants";
import { DropsWiseAuthorisationBackground } from "../../auth/components/dropswise-authorisation-background";
import { GuessMyTenantCode } from "../../auth/components/guess-my-tenant-code";
import {
  RanaAuthorisationBackground,
  RanaAuthorisationBackgroundProps,
} from "../../auth/components/rana-authorisation-background";
import { TenantContext } from "../../auth/providers/tenant-provider";
import { UndoIcon } from "../../icons";
import { DashboardIcon } from "../../icons/dashboardIcon";
import { RightArrowIcon } from "../../icons/rightArrowIcon";
import { ButtonWrapper } from "../components/button-wrapper";
import { RanaButton } from "../components/rana-button";
import { RanaHeading } from "../components/rana-heading";
import { useServicedeskMailto } from "../hooks/use-servicedesk-mailto";
import { RanaForm } from "../rana-form";
import { BORDER_WIDTHS } from "../theme/consts/border-width-consts";

import { SystemStatusLayout } from "./system-status-layout";

type BackgroundComponentType = ComponentType<
  RanaAuthorisationBackgroundProps & { children: ReactNode }
>;

type BaseProps = {
  BackgroundComponent?: BackgroundComponentType;
};

export type SystemStatusPageProps = BaseProps &
  // unhandled error is the default variant
  (| { variant?: "unhandled-error"; error: unknown; componentStack: string | null }
    | { variant: "not-found"; redirectUrl?: string }
    | { variant: "no-publication-found" }
    | { variant: "logged-out"; tenant: string }
    | {
        variant: "login";
        identityProviders: IdentityProvider[];
        redirectToLogin: (provider: string) => void;
        onCancel?: () => void;
      }
    | { variant: "welcome"; chooseTenantCode: (tenantCode: string) => void }
    | {
        variant: "dropswise-welcome";
        logoSrc: string;
        backgroundSrc: string;
        chooseTenantCode: (tenantCode: string) => void;
      }
    | { variant: "network-error" }
    | { variant: "no-subscription" }
  );

// Most system status pages have a similar backgroundComponent, except for welcome and login
function StatusScreen({
  BackgroundComponent,
  heading,
  explanation,
  instructions,
  actions,
}: {
  BackgroundComponent: BackgroundComponentType;
  heading: string;
  explanation: ReactNode;
  instructions: ReactNode;
  actions: ReactNode;
}) {
  return (
    <BackgroundComponent pageSubTitle={heading} logoMinWidth="3xl">
      <SystemStatusLayout
        heading={heading}
        explanation={explanation}
        instructions={instructions}
        actions={actions}
      />
    </BackgroundComponent>
  );
}

// Shown when an unhandled error occurs (Sentry error boundary fallback)
function UnhandledErrorContent({
  BackgroundComponent,
}: {
  BackgroundComponent: BackgroundComponentType;
}) {
  const intl = useIntl();

  const heading = intl.formatMessage({
    id: "error_page.heading",
    defaultMessage: "Something went wrong",
  });

  const mailtoLink = useServicedeskMailto(
    { id: "error_page.email.subject", defaultMessage: "Unexpected error" },
    {
      id: "error_page.email.body",
      defaultMessage:
        "Hi Rana support,\n\nI encountered an unexpected error on the following page: {link}\nCan you take a look?\n\nThanks!",
      values: { link: window.location.href },
    }
  );

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="error_page.unhandled_error.explanation"
          defaultMessage="An unexpected error occurred. Our engineers have been notified."
        />
      }
      instructions={
        <FormattedMessage
          id="error_page.unhandled_error.instructions"
          defaultMessage="Try reloading the page, or contact our Servicedesk if the problem persists."
        />
      }
      actions={
        <ButtonWrapper>
          <RanaButton
            variant="primary"
            leftIcon={<DashboardIcon boxSize={6} />}
            as="a"
            href="/"
            mr={6}
          >
            <FormattedMessage
              id="error_page.unhandled_error.button.go_home"
              defaultMessage="Go home"
            />
          </RanaButton>
          <RanaButton variant="secondary" as="a" href={mailtoLink}>
            <FormattedMessage
              id="error_page.unhandled_error.button.report"
              defaultMessage="Report a broken link"
            />
          </RanaButton>
        </ButtonWrapper>
      }
    />
  );
}

// Shown when a user navigates to a non-existing route
function NotFoundContent({
  BackgroundComponent,
  redirectUrl,
}: {
  BackgroundComponent: BackgroundComponentType;
  redirectUrl?: string;
}) {
  const intl = useIntl();
  const { tenant: tenantId } = useContext(TenantContext);
  const navigate = useNavigate();

  const heading = intl.formatMessage({
    id: "not_found_page.heading",
    defaultMessage: "Outside the catchment area",
  });

  const mailtoLink = useServicedeskMailto(
    { id: "not_found_page.email.subject", defaultMessage: "Broken link" },
    {
      id: "not_found_page.email.body",
      defaultMessage:
        "Hi Rana support,\n\nThe following link doesn't work for me: {link}\nCan you take a look?\n\nThanks!",
      values: { link: window.location.href },
    }
  );
  const redirectLink = redirectUrl ?? `/${tenantId}`;

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="not_found_page.explanation"
          defaultMessage="This page does not exist (anymore)"
        />
      }
      instructions={
        <FormattedMessage
          id="not_found_page.instructions"
          defaultMessage="Head back to your project or choose another direction."
        />
      }
      actions={
        <ButtonWrapper>
          {tenantId && (
            <RanaButton
              variant="primary"
              leftIcon={<DashboardIcon boxSize={6} />}
              mr={6}
              onClick={() => navigate(redirectLink)}
            >
              <FormattedMessage
                id="not_found_page.button.dashboard"
                defaultMessage="Return to dashboard"
              />
            </RanaButton>
          )}
          <RanaButton variant="secondary" as="a" href={mailtoLink}>
            <FormattedMessage
              id="not_found_page.button.report"
              defaultMessage="Report a broken link"
            />
          </RanaButton>
        </ButtonWrapper>
      }
    />
  );
}

// Shown when a user visits a non-existing publication link or has no access
function NoPublicationFoundContent({
  BackgroundComponent,
}: {
  BackgroundComponent: BackgroundComponentType;
}) {
  const intl = useIntl();
  const { tenant: tenantId } = useContext(TenantContext);
  const navigate = useNavigate();

  const heading = intl.formatMessage({
    id: "no_publication_found.heading",
    defaultMessage: "This publication link is invalid or expired",
  });

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="no_publication_found.explanation"
          defaultMessage="This publication does not exist (anymore), or you have no access"
        />
      }
      instructions={
        <FormattedMessage
          id="no_publication_found.instructions"
          defaultMessage="Head back to your project"
        />
      }
      actions={
        tenantId ? (
          <ButtonWrapper>
            <RanaButton
              variant="primary"
              leftIcon={<DashboardIcon boxSize={6} />}
              mr={6}
              onClick={() => navigate(`/${tenantId}`)}
            >
              <FormattedMessage
                id="not_found_page.button.dashboard"
                defaultMessage="Return to dashboard"
              />
            </RanaButton>
          </ButtonWrapper>
        ) : null
      }
    />
  );
}

// Shown after user is logged out
function LoggedOut({
  BackgroundComponent,
  tenant: tenantId,
}: {
  BackgroundComponent: BackgroundComponentType;
  tenant: string;
}) {
  const intl = useIntl();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const hasNoAccess = queryParams.get(ACCOUNT_HAS_NO_ACCESS_SEARCH_PARAM) === "true";

  const heading = hasNoAccess
    ? intl.formatMessage({
        id: "logout.no_access",
        defaultMessage: "This account has no access to this organisation",
      })
    : intl.formatMessage({
        id: "logged_out.goodbye",
        defaultMessage: "Goodbye",
      });

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="logged_out.you_have_been_signed_out"
          defaultMessage="You have been signed out"
        />
      }
      instructions={
        hasNoAccess ? (
          <FormattedMessage
            id="logged_out.instructions.no_access"
            defaultMessage="You can log in with a different account below"
          />
        ) : (
          <FormattedMessage
            id="logged_out.instructions"
            defaultMessage="You can log back in below"
          />
        )
      }
      actions={
        <ButtonWrapper>
          <RanaButton
            onClick={() => navigate(`/${tenantId}`)}
            variant="primary"
            rightIcon={<RightArrowIcon />}
          >
            <FormattedMessage id="logout.back_to_sign_in" defaultMessage="Go back to sign in" />
          </RanaButton>
        </ButtonWrapper>
      }
    />
  );
}

// Shown when a user needs to log in
function LoginContent({
  BackgroundComponent,
  identityProviders,
  redirectToLogin,
  onCancel,
}: {
  BackgroundComponent: BackgroundComponentType;
  identityProviders: IdentityProvider[];
  redirectToLogin: (provider: string) => void;
  onCancel?: () => void;
}) {
  const corporateIdentityProviders = identityProviders.filter((provider) => provider.id !== "rana");
  const hasCorporateIds = corporateIdentityProviders.length > 0;

  const longerTitleRef = useRef<HTMLDivElement>(null);
  const longerTitleRect = useRect(longerTitleRef);

  return (
    <BackgroundComponent
      pageSubTitle={<FormattedMessage id="status_page.login.sign_in" defaultMessage="Sign in" />}
      logoMinWidth="5xl"
      logoAlign={hasCorporateIds ? "left" : "center"}
    >
      <Flex flexDir="column" bg="brand.bg" p={6}>
        <Grid
          // Hide the corporate login option if there is no corporate ID available
          templateColumns={{ base: "1fr", md: `${hasCorporateIds ? "1fr 20px 1fr" : "1fr"}` }}
          my={11}
        >
          {hasCorporateIds && (
            <>
              <GridItem display="flex" flexDir="column" gap={4} px={8}>
                <RanaHeading
                  height={longerTitleRect.height ? `${longerTitleRect.height}px` : "auto"}
                  type="h4"
                  fontSize={{ base: 20, md: 24 }}
                >
                  <FormattedMessage
                    id="status_page.login.sign_in_with_corporate"
                    defaultMessage="Sign in with your SSO"
                  />
                </RanaHeading>
                {identityProviders
                  .filter((provider) => provider.id !== "rana")
                  .map((provider) => (
                    <RanaButton
                      key={provider.id}
                      variant="primary"
                      onClick={() => redirectToLogin(provider.id)}
                    >
                      {provider.name}
                    </RanaButton>
                  ))}
              </GridItem>
              <GridItem
                h={{ base: 11, md: "full" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
              >
                <Box
                  h={{ base: 0, md: "full" }}
                  w={BORDER_WIDTHS.base}
                  bg="brand.glacier"
                  position="absolute"
                />
                <Text color="brand.grey.600" bg="brand.bg" zIndex={1}>
                  <FormattedMessage id="text.or" defaultMessage="Or" />
                </Text>
              </GridItem>
            </>
          )}
          <GridItem display="flex" flexDir="column" gap={4} px={8}>
            <Box ref={longerTitleRef}>
              <RanaHeading type="h4" fontSize={{ base: 20, md: 24 }}>
                <FormattedMessage
                  id="status_page.sign_in_with_username"
                  defaultMessage="Sign in with your username and password"
                />
              </RanaHeading>
            </Box>
            <ButtonWrapper flexDir="column">
              <RanaButton w="full" variant="primary" onClick={() => redirectToLogin("rana")}>
                <FormattedMessage
                  id="status_page.button.rana_account"
                  defaultMessage="Rana account"
                />
              </RanaButton>
              {onCancel && (
                <RanaButton variant="fourthiary" onClick={onCancel} my={4}>
                  <FormattedMessage
                    id="status_page.button.cancel.need_an_account"
                    defaultMessage="Need an account? Sign up here."
                  />
                </RanaButton>
              )}
            </ButtonWrapper>
          </GridItem>
        </Grid>
      </Flex>
    </BackgroundComponent>
  );
}

// Organisation code form shared by the welcome variants
function WelcomeBox({
  chooseTenantCode,
  heading,
  submitLabel,
}: {
  chooseTenantCode: (tenantCode: string) => void;
  heading: ReactNode;
  submitLabel: ReactNode;
}) {
  const intl = useIntl();

  const [guessCodeBlockOpened, setGuessCodeBlockOpened] = useState(false);
  const [remember, setRemember] = useLocalStorage("rana.remember", true);
  const [organisationCodeInLocal, setOrganisationCodeInLocal] = useLocalStorage(
    "rana.organisationCode",
    ""
  );

  return (
    <Box background="brand.cloudy" w={{ base: "full", md: "2xl" }} p={6}>
      <RanaHeading type="h3" mb={4}>
        {heading}
      </RanaHeading>

      <FormattedMessage
        id="welcome_page.login.enter_organisation_code"
        defaultMessage="Please enter your organisation code."
      />
      <br />
      <FormattedMessage
        id="welcome_page.login.enter_organisation_code_description"
        defaultMessage="You have received this code in your invitation email. Click <a>here</a> if you did not receive it."
        values={{
          a: (text) => <Link href="mailto:support@ranawaterintelligence.com">{text}</Link>,
        }}
      />
      <RanaForm
        initialValues={{ organisationCode: organisationCodeInLocal }}
        doSubmit={({ organisationCode }: { organisationCode: string }) => {
          setOrganisationCodeInLocal(remember ? organisationCode.toLocaleLowerCase() : "");
          chooseTenantCode(organisationCode.toLocaleLowerCase());
        }}
      >
        <Box w="full" display="flex" flexDir="column" my={6}>
          <RanaForm.Input
            id="organisationCodeInput"
            field="organisationCode"
            label={intl.formatMessage({
              id: "welcome_page.login.organisation_code",
              defaultMessage: "Organisation code",
            })}
            explanation={intl.formatMessage({
              id: "welcome_page.login.organisation_code_placeholder",
              defaultMessage: "Enter your organisation code",
            })}
            isRequired
            isLowercase
            submitOnEnter
            mb={2}
          />
          <Checkbox
            isChecked={remember}
            onChange={(e) => {
              setRemember(e.target.checked);
            }}
          >
            <FormattedMessage
              id="welcome_page.login.remember_organisation_code"
              defaultMessage="Remember"
            />
          </Checkbox>
          <RanaForm.Errors />
          <HStack mt={6}>
            <Spacer />
            <RanaButton variant="secondary" onClick={() => setGuessCodeBlockOpened(true)}>
              I don&apos;t know my organisation code
            </RanaButton>
            <RanaForm.SubmitButton>{submitLabel}</RanaForm.SubmitButton>
          </HStack>
        </Box>
        {guessCodeBlockOpened && <GuessMyTenantCode />}
      </RanaForm>
    </Box>
  );
}

// Shown on the homepage to let users enter their organisation code
function WelcomeContent({
  BackgroundComponent,
  chooseTenantCode,
}: {
  BackgroundComponent: BackgroundComponentType;
  chooseTenantCode: (tenantCode: string) => void;
}) {
  return (
    <BackgroundComponent
      pageSubTitle={<FormattedMessage id="welcome_page.welcome" defaultMessage="Welcome" />}
      logoMinWidth="2xl"
    >
      <WelcomeBox
        chooseTenantCode={chooseTenantCode}
        heading={<FormattedMessage id="invitation.welcome" defaultMessage="Hi, welcome to rana" />}
        submitLabel={
          <FormattedMessage
            id="welcome_page.login.signin"
            defaultMessage="Sign in to your rana organisation"
          />
        }
      />
    </BackgroundComponent>
  );
}

// Welcome page with DropsWise branding, shown for demo logins
function DropsWiseWelcomeContent({
  logoSrc,
  backgroundSrc,
  chooseTenantCode,
}: {
  logoSrc: string;
  backgroundSrc: string;
  chooseTenantCode: (tenantCode: string) => void;
}) {
  return (
    <DropsWiseAuthorisationBackground
      pageSubTitle={<FormattedMessage id="welcome_page.welcome" defaultMessage="Welcome" />}
      logoSrc={logoSrc}
      backgroundSrc={backgroundSrc}
    >
      <WelcomeBox
        chooseTenantCode={chooseTenantCode}
        heading={
          <FormattedMessage
            id="welcome_page.dropswise.welcome"
            defaultMessage="Hi, welcome to DropsWise"
          />
        }
        submitLabel={
          <FormattedMessage
            id="welcome_page.login.signin_dropswise"
            defaultMessage="Sign in to your organisation"
          />
        }
      />
    </DropsWiseAuthorisationBackground>
  );
}

// shown when the internet connection is broken while the user is running the application
function NetworkErrorContent({
  BackgroundComponent,
}: {
  BackgroundComponent: BackgroundComponentType;
}) {
  const intl = useIntl();
  const heading = intl.formatMessage({
    id: "network_error_page.heading",
    defaultMessage: "Something went wrong",
  });

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="network_error_page.explanation"
          defaultMessage="We couldn't complete your request."
        />
      }
      instructions={
        <FormattedMessage
          id="network_error_page.instructions"
          defaultMessage="Please check your network connection and try again."
        />
      }
      actions={
        <RanaButton
          onClick={() => window.location.reload()}
          variant="primary"
          rightIcon={<UndoIcon />}
        >
          <FormattedMessage id="network_error_page.try_again" defaultMessage="Try again" />
        </RanaButton>
      }
    />
  );
}

// shown when organisation does not have subscription for use Rana Live
function NoRanaSubscriptioncontent({
  BackgroundComponent,
}: {
  BackgroundComponent: BackgroundComponentType;
}) {
  const intl = useIntl();

  const heading = intl.formatMessage({
    id: "error_page.no_rana_subscription.heading",
    defaultMessage: "Your organisation does not have a subscription for Rana Live",
  });

  const mailtoLink = useServicedeskMailto(
    { id: "error_page.no_rana_subscription.email.subject", defaultMessage: "Unexpected error" },
    {
      id: "error_page.no_rana_subscription.email.body",
      defaultMessage:
        "Hi Rana support,\n\nIt seems my organisation does not have a Rana Live subscription.\nCan you take a look?\n\nThanks!",
    }
  );

  return (
    <StatusScreen
      BackgroundComponent={BackgroundComponent}
      heading={heading}
      explanation={
        <FormattedMessage
          id="error_page.no_rana_subscription.explanation"
          defaultMessage="Please contact the Service Desk for more information"
        />
      }
      instructions={
        <FormattedMessage
          id="error_page.no_rana_subscription.instructions"
          defaultMessage="Or return to your dashboard"
        />
      }
      actions={
        <ButtonWrapper>
          <RanaButton
            variant="primary"
            as="a"
            href={mailtoLink}
            leftIcon={<EmailIcon boxSize={6} />}
          >
            <FormattedMessage
              id="error_page.no_rana_subscription.button.contact"
              defaultMessage="Contact service desk"
            />
          </RanaButton>
          <RanaButton
            variant="secondary"
            leftIcon={<DashboardIcon boxSize={6} />}
            as="a"
            href="/"
            mr={6}
          >
            <FormattedMessage
              id="error_page.no_rana_subscription.button.return"
              defaultMessage="Return to dashboard"
            />
          </RanaButton>
        </ButtonWrapper>
      }
    />
  );
}

export function SystemStatusPage(props: SystemStatusPageProps) {
  const BackgroundComponent = props.BackgroundComponent ?? RanaAuthorisationBackground;

  if (props.variant === "not-found") {
    return (
      <NotFoundContent BackgroundComponent={BackgroundComponent} redirectUrl={props.redirectUrl} />
    );
  }

  if (props.variant === "no-publication-found") {
    return <NoPublicationFoundContent BackgroundComponent={BackgroundComponent} />;
  }

  if (props.variant === "logged-out") {
    return <LoggedOut BackgroundComponent={BackgroundComponent} tenant={props.tenant} />;
  }

  if (props.variant === "login") {
    return (
      <LoginContent
        BackgroundComponent={BackgroundComponent}
        identityProviders={props.identityProviders}
        redirectToLogin={props.redirectToLogin}
        onCancel={props.onCancel}
      />
    );
  }

  if (props.variant === "welcome") {
    return (
      <WelcomeContent
        BackgroundComponent={BackgroundComponent}
        chooseTenantCode={props.chooseTenantCode}
      />
    );
  }

  if (props.variant === "dropswise-welcome") {
    return (
      <DropsWiseWelcomeContent
        logoSrc={props.logoSrc}
        backgroundSrc={props.backgroundSrc}
        chooseTenantCode={props.chooseTenantCode}
      />
    );
  }

  if (props.variant === "network-error") {
    return <NetworkErrorContent BackgroundComponent={BackgroundComponent} />;
  }

  if (props.variant === "no-subscription") {
    return <NoRanaSubscriptioncontent BackgroundComponent={BackgroundComponent} />;
  }

  return <UnhandledErrorContent BackgroundComponent={BackgroundComponent} />;
}

// Backward-compat aliases
export { SystemStatusPage as ErrorPage };
export type { SystemStatusPageProps as ErrorPageProps };
